import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * APC Store Stock component.
 *
 * Fetches variant inventory levels from Gadget and displays
 * available store locations in a drawer dialog with search filtering.
 *
 * @extends Component
 */
class ApcStoreStock extends Component {
  static ENDPOINT = '/apps/apa/api/store-stock';

  /** Locations excluded from results */
  static EXCLUDED_NAMES = ['H.Q', 'Online', 'Outlet'];

  #allLocations = [];
  #abortController = null;
  #config = null;
  #dialogComponent = null;
  #searchInput = null;
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    this.#config = this.#parseConfig();
    this.#dialogComponent = this.querySelector('dialog-component');
    this.#searchInput = this.querySelector('.apc-store-stock__search-input');
    this.#section = this.closest('.shopify-section, dialog');

    this.#dialogComponent?.addEventListener('dialog:open', this.#handleOpen);
    this.#searchInput?.addEventListener('input', this.#handleSearch);
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#dialogComponent?.removeEventListener('dialog:open', this.#handleOpen);
    this.#searchInput?.removeEventListener('input', this.#handleSearch);
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * Keeps the variant ID in sync when the shopper changes variant.
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    if (variant?.id) this.dataset.variantId = variant.id;
  };

  /**
   * Parses block settings from the JSON config script.
   * @returns {object}
   */
  #parseConfig() {
    const script = this.querySelector('script[type="application/json"]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent);
    } catch {
      return {};
    }
  }

  /**
   * Returns the current variant ID from the data attribute.
   * Kept current by #onVariantUpdate as the shopper changes variant.
   * @returns {string}
   */
  get variantId() {
    return this.dataset.variantId || '';
  }

  /**
   * Fetches store stock when the dialog opens.
   */
  #handleOpen = async () => {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.#showLoading();

    try {
      const response = await fetch(`${ApcStoreStock.ENDPOINT}?variantId=${this.variantId}`, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();

      this.#allLocations = (data.locations || [])
        .filter((loc) => loc.quantity > 0)
        .filter((loc) => !ApcStoreStock.EXCLUDED_NAMES.some((name) => loc.name.includes(name)));

      this.#renderLocations(this.#allLocations);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('APC Store Stock: Failed to fetch locations:', err);
      this.#showError();
    }
  };

  /**
   * Filters displayed locations based on search input.
   */
  #handleSearch = () => {
    const query = this.#searchInput.value.trim().toLowerCase();

    if (!query) {
      this.#renderLocations(this.#allLocations);
      return;
    }

    const filtered = this.#allLocations.filter((loc) => loc.name.toLowerCase().includes(query));

    this.#renderLocations(filtered);
  };

  /**
   * Renders a list of locations into the DOM.
   * @param {Array} locations
   */
  #renderLocations(locations) {
    const { loading, locationsEl, empty, error } = this.#getElements();

    loading.hidden = true;
    error.hidden = true;

    if (locations.length === 0) {
      locationsEl.innerHTML = '';
      empty.hidden = false;
      return;
    }

    empty.hidden = true;
    const inStockLabel = this.#config?.in_stock_label || 'in stock';

    locationsEl.innerHTML = locations
      .map(
        (loc) => `
        <li class="apc-store-stock__location">
          <div class="apc-store-stock__location-header">
            <p class="apc-store-stock__location-name">${loc.name}</p>
            <span class="apc-store-stock__location-quantity">${loc.quantity} ${inStockLabel}</span>
          </div>
          ${loc.address?.phone ? `<a href="tel:${loc.address.phone}" class="apc-store-stock__location-phone">${loc.address.phone}</a>` : ''}
        </li>`,
      )
      .join('');
  }

  #showLoading() {
    const { loading, locationsEl, empty, error } = this.#getElements();
    loading.hidden = false;
    locationsEl.innerHTML = '';
    empty.hidden = true;
    error.hidden = true;
    if (this.#searchInput) this.#searchInput.value = '';
  }

  #showError() {
    const { loading, locationsEl, empty, error } = this.#getElements();
    loading.hidden = true;
    locationsEl.innerHTML = '';
    empty.hidden = true;
    error.hidden = false;
  }

  #getElements() {
    return {
      loading: this.querySelector('.apc-store-stock__loading'),
      locationsEl: this.querySelector('.apc-store-stock__locations'),
      empty: this.querySelector('.apc-store-stock__empty'),
      error: this.querySelector('.apc-store-stock__error'),
    };
  }
}

if (!customElements.get('apc-store-stock')) {
  customElements.define('apc-store-stock', ApcStoreStock);
}

export { ApcStoreStock };
